import { InputBase, ListItemButton, ListItemIcon } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AutoSizer, List } from "react-virtualized";

import { getDataTreeUrl } from "@helpers/constants";

import TruncatedTextWithTooltip from "../../../components/TruncatedTextWithTooltip";
import { getDataFolderStream } from "../helpers/api";
import type { ClientFile, FileType } from "../helpers/utils";
import { DataContext } from "../pages/Data";
import FileIcon from "./FileIcon";
import Loading from "./Loading";
import Preview from "./Preview";

type DataColumnProps = {
  dataId: string;
  path: string;
  index: number;
  activeSegment?: string;
  type?: FileType;
};

const ListContainer = styled("div")({
  minWidth: "300px",
  maxWidth: "300px",
  borderRight: "1px solid lightgrey",
  display: "grid",
  gridTemplateRows: "auto 1fr",
  position: "relative",
});

const FilterContainer = styled("div")({
  borderBottom: "1px solid lightgrey",
  padding: ".25rem 1rem",
  display: "flex",
  "& .MuiInputBase-root": { width: "100%", fontSize: "1rem" },
});

const StyledListItemButton = styled(ListItemButton)({ whiteSpace: "nowrap" });

function filesReducer(files: ClientFile[], moreFiles: ClientFile[]) {
  return files.concat(moreFiles);
}

const DataColumn = (props: DataColumnProps) => {
  const navigate = useNavigate();
  const { file, setFile } = useContext(DataContext);
  const [filesLoading, setFilesLoading] = useState(true);
  const [fileResponseStarted, setFileResponseStarted] = useState(false);
  const [files, addMoreFiles] = useReducer(filesReducer, []);
  const directoryName =
    file && file.kind !== "directory"
      ? props.path.replace(new RegExp(`${file.name}$`), "")
      : props.path;
  useEffect(() => {
    const dataFolderStream = getDataFolderStream(props.dataId, directoryName);
    const clientFileReader = dataFolderStream.getReader();
    let cancelled = false;

    // TODO(HOPE): See if there is a better way to do this
    (async () => {
      while (!cancelled) {
        const { done, value } = await clientFileReader.read();
        if (cancelled) {
          clientFileReader.releaseLock();
          dataFolderStream.cancel();
          break;
        }
        setFileResponseStarted(true);
        if (done || !value || !value.length) {
          setFilesLoading(false);
          break;
        }
        addMoreFiles(value);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [props.dataId, directoryName]);
  const [filter, setFilter] = useState("");
  const filterLowerCase = filter.toLowerCase();
  const list =
    files.filter((file) => file.name.toLowerCase().includes(filterLowerCase)) ||
    [];
  const activeIndex = list.findIndex((f) => f.name === props.activeSegment);
  // If we'd like to create more of a navigational component for the file directory we can remove replace:true below
  const navigateToItem = useCallback(
    (filepath: string) => {
      navigate(getDataTreeUrl(props.dataId, filepath), {
        state: { canGoBack: true },
        replace: true,
      });
    },
    [navigate, props.dataId],
  );

  useEffect(() => {
    if (props.index === 1 && files.length) {
      const file = files.find((file) => file.name === props.activeSegment);
      if (file) {
        setFile && setFile(file);
      }
    }
  }, [files, props.activeSegment, props.index, setFile]);

  if (props.index === 0 && file?.kind === "file") {
    return <Preview />;
  }

  return (
    <ListContainer className="column">
      <FilterContainer>
        <InputBase
          placeholder="Filter"
          value={filter}
          inputProps={{ "aria-label": "Filter" }}
          onChange={(e) => setFilter(e.target.value)}
        />
      </FilterContainer>
      {fileResponseStarted && (
        <div>
          <AutoSizer>
            {({ width, height }) => (
              <List
                width={width}
                height={height}
                rowHeight={37}
                rowCount={list.length}
                scrollToIndex={activeIndex}
                rowRenderer={({ index: itemIndex, key, style }) => {
                  const item = list[itemIndex];
                  return (
                    <StyledListItemButton
                      key={key}
                      style={style}
                      onClick={() =>
                        navigateToItem(
                          [props.path, item.name].filter(Boolean).join("/"),
                        )
                      }
                      selected={item.name === props.activeSegment}
                    >
                      <ListItemIcon>
                        <FileIcon type={item.type} />
                      </ListItemIcon>
                      <TruncatedTextWithTooltip
                        text={item.name}
                        placement="bottom-start"
                      />
                    </StyledListItemButton>
                  );
                }}
              />
            )}
          </AutoSizer>
        </div>
      )}
      {filesLoading && <Loading />}
    </ListContainer>
  );
};

export default DataColumn;
